/**
 * PushOptIn — reusable card that asks the shopper to enable browser
 * notifications for their favourite brands. Silent when push isn't supported
 * (iOS Safari < 16.4 in-browser, etc.) or when the user already opted in.
 */
import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const supported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

// Convert URL-safe base64 to Uint8Array (Push API requirement).
function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export default function PushOptIn() {
  const { user } = useAuth();
  const [state, setState] = useState("unknown"); // unknown, unsupported, denied, granted, subscribed
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!supported()) return setState("unsupported");
      if (Notification.permission === "denied") return setState("denied");
      if (Notification.permission !== "granted") return setState("prompt");
      // permission granted — check if we already have an active subscription
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return setState("prompt");
      const sub = await reg.pushManager.getSubscription();
      setState(sub ? "subscribed" : "prompt");
    })();
  }, []);

  const enable = async () => {
    if (!user) { toast.error("Sign in first to enable push."); return; }
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      // Wait for the sw to become active before subscribing.
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permission denied — you can enable it later in your browser settings.");
        setState("denied");
        return;
      }
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.configured) throw new Error("Server key missing");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.key),
      });
      const raw = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
      });
      setState("subscribed");
      toast.success("Push notifications enabled. We'll ping you when a favourite brand drops a new best offer.");
    } catch (e) {
      toast.error("Could not enable push notifications.");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await api.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setState("prompt");
      toast.success("Push notifications turned off.");
    } catch { toast.error("Could not disable push"); }
    finally { setBusy(false); }
  };

  const testPush = async () => {
    setBusy(true);
    try {
      const r = await api.post("/push/test");
      toast.success(`Test sent to ${r.data.sent} device${r.data.sent === 1 ? "" : "s"}. Check your notification tray.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setBusy(false); }
  };

  if (state === "unsupported" || state === "unknown") return null;

  if (state === "subscribed") {
    return (
      <div className="pf-card p-4 flex items-center gap-3 bg-emerald-50/60 border-emerald-200/60"
           data-testid="push-optin-card">
        <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
          <Bell className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 text-sm">Push notifications on</div>
          <div className="text-xs text-slate-600">We'll wake you when a favourite brand drops a better offer.</div>
        </div>
        <button onClick={testPush} disabled={busy}
                className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold"
                data-testid="push-test-btn">Test</button>
        <button onClick={disable} disabled={busy}
                className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                data-testid="push-disable-btn">
          <BellOff className="w-3 h-3" /> Turn off
        </button>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div className="pf-card p-4 flex items-center gap-3 bg-amber-50/60 border-amber-200/60"
           data-testid="push-optin-card">
        <BellOff className="w-5 h-5 text-amber-600" />
        <div className="text-xs text-slate-700 flex-1">
          Push notifications are blocked. Enable them in your browser's site settings, then reload.
        </div>
      </div>
    );
  }

  return (
    <div className="pf-card p-4 flex items-center gap-3 bg-cyan-50/60 border-cyan-200/60"
         data-testid="push-optin-card">
      <div className="w-10 h-10 rounded-xl bg-cyan-500 text-white flex items-center justify-center shrink-0">
        <Bell className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 text-sm">Get pinged when your brands drop a better deal</div>
        <div className="text-xs text-slate-600">One-tap opt-in — we'll never ping you for anything else.</div>
      </div>
      <button onClick={enable} disabled={busy}
              className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="push-enable-btn">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
        Enable push
      </button>
    </div>
  );
}
